import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Expertise from "./pages/Expertise";
import HowWeWork from "./pages/HowWeWork";
import Industries from "./pages/Industries";
import Insights from "./pages/Insights";
import Legal from "./pages/Legal";

/**
 * Set to false to fall back to plain full-page navigation between routes
 * (i.e. behave like the original multi-page site again) with no other
 * code changes. See PageShell.tsx for why this exists.
 */
const SPA_NAVIGATION = true;

/**
 * The original pages link to each other with ordinary <a href="..."> tags
 * (now pointing at routes like "/about" instead of the old .html files -
 * see extract.py). Rather than hand-converting every anchor in that
 * markup to a React <Link>, this delegates a single click listener on the
 * document: any click on an internal link that changes the *path* is
 * turned into a client-side navigation. Clicks on plain "#fragment" links,
 * external links, and links with target="_blank" are left completely
 * alone and behave exactly as the static HTML always did.
 */
function InternalLinkInterceptor() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!SPA_NAVIGATION) return;

    function onClick(e: MouseEvent) {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as HTMLElement)?.closest?.("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/")) return; // external, mailto:, tel:, or "#frag"
      if (anchor.target && anchor.target !== "_self") return;

      const url = new URL(href, window.location.origin);
      if (url.pathname === location.pathname) return; // same-page anchor, let it scroll natively

      e.preventDefault();
      navigate(href);
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate, location.pathname]);

  return null;
}

export default function App() {
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <InternalLinkInterceptor />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/expertise" element={<Expertise />} />
        <Route path="/how-we-work" element={<HowWeWork />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/legal" element={<Legal />} />
      </Routes>
    </BrowserRouter>
  );
}
